import {
  Block,
  BlockComment,
  BlockStatusCode,
  CommodityAssignee,
  CrewMemberAssignment,
  Facility,
  ResolveBlockError,
  ResolveBlockInput,
  ResolveBlockPayload,
} from '@bechtel/types';
import {
  SideSheetFooter,
  ButtonAppearance,
  Spinner,
  Box,
  ToastAction,
  ToastType,
  useToastsDispatch,
} from '@bechtel/gds';
import { t, Trans } from '@lingui/macro';
import { object, string } from 'yup';
import { Formik } from 'formik';
import { DrawerFooterButtonStyled } from '@bechtel/client-common';
import { ApolloError, useMutation } from '@apollo/client';
import { RESOLVE_BLOCK } from '@bechtel/client-gql';
import { FC, useState } from 'react';
import { get } from 'lodash';
import { BlockNotResolved } from '~/components/molecules';
import { SingleBlock } from '../resolve-single-block';

interface FormikBlockResolutionState {
  resolution: BlockComment['comment'];
}
const blockResolutionValidationSchema = object().shape({
  resolution: string()
    .max(2000, t`Please limit resolution notes to 2000 characters`)
    .required(t`Resolution Note is mandatory`),
});

const blockResolutionInitialValues: FormikBlockResolutionState = {
  resolution: '',
};

interface ResolveBlockFormProps {
  block: Block;
  assignedCrewMembers: CrewMemberAssignment[];
  facilities: Facility[];
  workPackageId: string;
  weldCommodityAssignees: CommodityAssignee[];
  onClose: () => void;
}

export const ResolveBlockForm: FC<ResolveBlockFormProps> = ({
  block,
  assignedCrewMembers,
  facilities,
  workPackageId,
  weldCommodityAssignees,
  onClose,
}) => {
  const [resolveBlockError, setResolveBlockError] = useState<ResolveBlockError | ApolloError>();
  const dispatchToast = useToastsDispatch();
  const [resolveBlock, { loading: resolveBlockLoading }] = useMutation<
    { resolveBlock: ResolveBlockPayload },
    { input: ResolveBlockInput }
  >(RESOLVE_BLOCK, {
    onCompleted: (data) => {
      const resolveBlockError = get(data, 'resolveBlock.error');
      if (resolveBlockError) {
        setResolveBlockError(resolveBlockError);
      } else if (get(data, 'resolveBlock.block')) {
        const resolveBlockToast: ToastAction = {
          type: 'ADD_TOAST',
          payload: {
            toastType: ToastType.SUCCESS,
            content: t`Block Successfully Resolved`,
            autoDismissTimeout: 5000,
          },
        };
        dispatchToast(resolveBlockToast);
        onClose();
      }
    },
    onError: (error) => {
      setResolveBlockError(error);
    },
  });

  const closeBlockErrorModal = () => {
    setResolveBlockError(undefined);
  };

  return (
    <>
      {resolveBlockError && (
        <BlockNotResolved
          workPackageId={workPackageId}
          resolveBlockError={resolveBlockError}
          onClose={closeBlockErrorModal}
        />
      )}
      <Formik
        enableReinitialize={true}
        initialValues={blockResolutionInitialValues}
        validationSchema={blockResolutionValidationSchema}
        onSubmit={(values: FormikBlockResolutionState) => {
          if (block && block.id && values && values.resolution) {
            resolveBlock({
              variables: {
                input: {
                  id: block.id,
                  comment: values.resolution,
                },
              },
            });
          }
        }}
      >
        {(formikProps) => {
          return (
            <>
              <SingleBlock
                blockId={block.id}
                assignedCrewMembers={assignedCrewMembers}
                weldCommodityAssignees={weldCommodityAssignees}
                facilities={facilities}
              />
              <SideSheetFooter>
                <Box justifyContent={{ sm: 'flex-end' }} gap={{ sm: '500' }}>
                  <Box>
                    <DrawerFooterButtonStyled appearance={ButtonAppearance.SECONDARY} onClick={() => onClose()}>
                      <Trans>Cancel</Trans>
                    </DrawerFooterButtonStyled>
                  </Box>
                  {block.currentStatusI18n === BlockStatusCode.BLOCK_OPENED && (
                    <Box>
                      <DrawerFooterButtonStyled
                        disabled={!formikProps.dirty || !formikProps.isValid || !formikProps.values.resolution}
                        onClick={() => formikProps.handleSubmit()}
                        appearance={ButtonAppearance.SECONDARY}
                      >
                        {resolveBlockLoading && <Spinner />}
                        {!resolveBlockLoading && <Trans>Resolve Block</Trans>}
                      </DrawerFooterButtonStyled>
                    </Box>
                  )}
                </Box>
              </SideSheetFooter>
            </>
          );
        }}
      </Formik>
    </>
  );
};
